import { z } from 'zod';

/**
 * Ticket state enum - mirrors Prisma EstadoOt enum exactly.
 * 7 states in the SIME ticket lifecycle.
 */
export const EstadoOtSchema = z.enum([
  'PROGRAMADO',
  'INICIADO',
  'NO_EJECUTADO',
  'EJECUTADO',
  'APROBADO',
  'RECHAZADO',
  'REPROGRAMADO',
]);

export type EstadoOt = z.infer<typeof EstadoOtSchema>;

/**
 * Whether a ticket's PDF exists and, if so, whether it carries a digital
 * signature. `FIRMADO` is the contractual artifact the Codelco coordinator
 * calls "el certificado"; `SIN_FIRMAR` is a generated-but-unsigned draft.
 *
 * Deliberately NOT derived from `estadoOt`. Every list surface used to infer
 * "has PDF" from `estadoOt ∈ {EJECUTADO, APROBADO}`, which on production INS
 * data paints a PDF icon on 273 legacy EJECUTADO tickets of which 264 have no
 * document at all, and makes 400 unsigned APROBADO drafts indistinguishable
 * from 7.760 signed certificates. The state of the workflow and the state of
 * the paperwork are different facts; only the DB knows the second one.
 */
export const PdfEstadoSchema = z.enum(['FIRMADO', 'SIN_FIRMAR']);

export type PdfEstado = z.infer<typeof PdfEstadoSchema>;

/**
 * Per-ticket document availability, as surfaced by the list/calendar endpoints
 * so a row can be rendered truthfully without an extra request per ticket.
 *
 * Two different artifacts, deliberately not merged:
 *  - `certificadoCount` — the **certificados adjuntos**: certification
 *    documents uploaded against the ticket (`ot_certificado`). This is what
 *    the Codelco coordinator means by "el certificado". Same name and same
 *    rule as `MiniAppHistoricoTicketSchema.certificadoCount` so the main app
 *    and the coordinator miniapp count the same rows: not deleted, non-empty
 *    S3 key.
 *  - `pdfEstado` — the ticket's own work-order PDF (`verificacion_documento`),
 *    signed or draft. A different document; kept apart so a signed work order
 *    is never mistaken for a certification.
 */
export const TicketDocumentosSchema = z.object({
  pdfEstado: PdfEstadoSchema.nullable(),
  certificadoCount: z.number().int().nonnegative(),
});

export type TicketDocumentos = z.infer<typeof TicketDocumentosSchema>;

/**
 * Request body for ticket state transitions.
 * Used by PATCH /api/v1/tickets/:id/transition
 */
export const TransitionBodySchema = z.object({
  targetState: EstadoOtSchema,
  reason: z.string().max(500).optional(),
  metadata: z.record(z.unknown()).optional(),
});

export type TransitionBody = z.infer<typeof TransitionBodySchema>;

/**
 * Transition result returned by transitionTicket().
 * Discriminated union on `success` for type-safe handling.
 */
export const TransitionResultSchema = z.discriminatedUnion('success', [
  z.object({
    success: z.literal(true),
    fromState: EstadoOtSchema,
    toState: EstadoOtSchema,
  }),
  z.object({
    success: z.literal(false),
    error: z.string(),
    fromState: EstadoOtSchema.optional(),
  }),
]);

export type TransitionResult = z.infer<typeof TransitionResultSchema>;

/**
 * Multi-estado query filter. Arrives from the query string as CSV
 * ("EJECUTADO,APROBADO") via URLSearchParams, or as a real array (repeated
 * params); normalizes to a non-empty `EstadoOt[]`. Shared by the ticket list
 * filter and the tickets export query so the parsing rule never diverges
 * between layers (single source — same idea as REPORTABLE_ESTADOS).
 */
export const EstadoOtInSchema = z.preprocess(
  (v) =>
    typeof v === 'string'
      ? v
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : v,
  z.array(EstadoOtSchema).min(1),
);

/**
 * Ticket query filters for list endpoints.
 */
export const TicketFilterSchema = z.object({
  estadoOt: EstadoOtSchema.optional(),
  // Multi-estado filter (e.g. report-tickets default = EJECUTADO + APROBADO).
  // Takes precedence over the singular `estadoOt`.
  estadoOtIn: EstadoOtInSchema.optional(),
  ambitoId: z.coerce.number().int().positive().optional(),
  periodo: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Formato: YYYY-MM').optional(),
  periodoDesde: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Formato: YYYY-MM').optional(),
  periodoHasta: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Formato: YYYY-MM').optional(),
  supervisorId: z.coerce.number().int().positive().optional(),
  empresaId: z.coerce.number().int().positive().optional(),
  tipoEquipoId: z.coerce.number().int().positive().optional(),
  catalogoecoId: z.coerce.number().int().positive().optional(),
  search: z.string().trim().min(1).max(100).optional(),
});

export type TicketFilter = z.infer<typeof TicketFilterSchema>;

/**
 * Request body for creating a new ticket.
 */
export const CreateTicketSchema = z.object({
  empresaId: z.number().int().positive(),
  subcontratoId: z.number().int().positive(),
  ambitoId: z.number().int().positive(),
  ubicacionId: z.number().int().positive(),
  tipoOtId: z.number().int().positive(),
  tipoMantenimientoId: z.number().int().positive(),
  pautasId: z.number().int().positive().optional().nullable(),
  // turnosId removed: a ticket's turno is derived from fechaProgramada on read
  // (resolveTicketTurno), never supplied at creation.
  periodo: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Formato: YYYY-MM'),
  fechaProgramada: z.string().datetime().optional(),
  planMatrizId: z.number().int().positive().optional(),
  observaciones: z.string().max(2000).optional(),
  operadorId: z.number().int().positive().optional(),
  encargadoId: z.number().int().positive().optional(),
  prioridad: z.number().int().min(1).max(5).optional(),
  ticketPadreId: z.number().int().positive().optional(),
  offlineId: z.string().max(255).optional(),
});
export type CreateTicketInput = z.infer<typeof CreateTicketSchema>;

/**
 * Request body for updating an existing ticket (all fields optional).
 */
export const UpdateTicketSchema = CreateTicketSchema.partial();
export type UpdateTicketInput = z.infer<typeof UpdateTicketSchema>;
